"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isNodeSelection,
} from "lexical";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode, $isListNode } from "@lexical/list";
import { $isTableNode } from "@lexical/table";
import { $isImageBlockNode } from "../../kernel/nodes/ImageBlockNode";
import { findClosestBlockNodeKey } from "../../kernel/helpers/findClosestBlockNodeKey";
import type { BlockHighlightRect } from "./blockHighlightRect";
import { computeDragMenuViewportAnchorForKey } from "./blockHandleLayout";

export type FloatingPos = { top: number; left: number } | null;

export function useBlockHandleOverlayState({
  isEditable,
  containerRef,
}: {
  isEditable: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [editor] = useLexicalComposerContext();

  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const [activeIsListItem, setActiveIsListItem] = React.useState(false);
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
  const [allRects, setAllRects] = React.useState<BlockHighlightRect[]>([]);

  const [dragMenuOpen, setDragMenuOpen] = React.useState(false);
  const [dragMenuPos, setDragMenuPos] = React.useState<FloatingPos>(null);
  const [dragMenuKey, setDragMenuKey] = React.useState<string | null>(null);
  /** recompute 内读取，避免闭包滞后；菜单打开时锚点必须跟 dragMenuKey 而非仅 Lexical caret */
  const dragMenuOpenRef = React.useRef(false);
  const dragMenuKeyRef = React.useRef<string | null>(null);
  /** pointerdown～pointerup 落在块手柄上：此间选区常为 null，recompute 不得清空（否则早于 onPress 抹掉即将打开的菜单） */
  const blockHandlePressSessionRef = React.useRef(false);
  React.useEffect(() => {
    dragMenuOpenRef.current = dragMenuOpen;
  }, [dragMenuOpen]);
  React.useEffect(() => {
    dragMenuKeyRef.current = dragMenuKey;
  }, [dragMenuKey]);

  React.useEffect(() => {
    if (!containerRef.current) return;

    let scheduleRaf = 0;

    function recompute() {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        let nextActiveKey: string | null = null;
        if ($isRangeSelection(selection)) {
          nextActiveKey = findClosestBlockNodeKey(selection.anchor.getNode());
        } else if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          if (nodes.length === 1 && $isImageBlockNode(nodes[0])) nextActiveKey = nodes[0].getKey();
        }
        // 选区为 null 等：仍刷新 allRects（图片异步加载后无 Lexical update 会导致旧矩形命中失败）

        setActiveKey(nextActiveKey);
        if (nextActiveKey) {
          const activeNode = $getNodeByKey(nextActiveKey);
          setActiveIsListItem($isListItemNode(activeNode));
        } else {
          setActiveIsListItem(false);
        }

        const container = containerRef.current;
        if (!container) return;
        const cRect = container.getBoundingClientRect();

        const root = $getRoot();
        const topBlocks: any[] = [];
        const walk = (node: any) => {
          if ($isListItemNode(node)) {
            topBlocks.push(node);
            // 仍要遍历子级中的嵌套 ListNode，否则子列表项不会进入 allRects，嵌套拖拽/落点全部失效
            const liChildren = node?.getChildren?.() ?? [];
            for (const child of liChildren) {
              if ($isListNode(child)) {
                for (const subItem of child.getChildren?.() ?? []) {
                  walk(subItem);
                }
              }
            }
            return;
          }
          if ($isImageBlockNode(node)) {
            topBlocks.push(node);
            return;
          }
          if ($isTableNode(node)) {
            topBlocks.push(node);
            return;
          }
          if ($isParagraphNode(node) || $isHeadingNode(node)) {
            const parent = node?.getParent?.();
            if ($isListItemNode(parent)) return; // 列表内部段落不单独作为块
            topBlocks.push(node);
          }
          const children = node?.getChildren?.() ?? [];
          for (const child of children) walk(child);
        };
        for (const child of root.getChildren()) walk(child);
        const nextAllRects: BlockHighlightRect[] = [];
        for (const n of topBlocks) {
          const key = n.getKey();
          const el = editor.getElementByKey(key);
          if (!el) continue;
          const r = el.getBoundingClientRect();
          nextAllRects.push({
            key,
            top: r.top - cRect.top + container.scrollTop,
            left: r.left - cRect.left + container.scrollLeft,
            width: r.width,
            height: r.height,
          });
        }

        setAllRects(nextAllRects);

        // 点击拖拽手柄会让 contenteditable 失焦，选区常变为 null；不得因此关掉刚打开的块菜单，也不得在手柄 onPress 之前清空。
        if (!nextActiveKey) {
          if (dragMenuOpenRef.current) {
            const menuKey = dragMenuKeyRef.current;
            if (menuKey) {
              const menuPos = computeDragMenuViewportAnchorForKey(menuKey, editor, container);
              if (menuPos) setDragMenuPos(menuPos);
            }
          } else if (!blockHandlePressSessionRef.current) {
            dragMenuKeyRef.current = null;
            setDragMenuOpen(false);
            setDragMenuPos(null);
            setDragMenuKey(null);
            setSelectedKeys([]);
          }
          return;
        }

        const keyForMenuPos =
          dragMenuOpenRef.current && dragMenuKeyRef.current ? dragMenuKeyRef.current : nextActiveKey;
        const menuPos = computeDragMenuViewportAnchorForKey(keyForMenuPos, editor, container);
        if (menuPos) setDragMenuPos(menuPos);
        else setDragMenuPos(null);
      });
    }

    function scheduleRecompute() {
      if (scheduleRaf) return;
      scheduleRaf = window.requestAnimationFrame(() => {
        scheduleRaf = 0;
        recompute();
      });
    }

    let rootResizeObserver: ResizeObserver | undefined;
    const removeRootListener = editor.registerRootListener((rootElement) => {
      rootResizeObserver?.disconnect();
      rootResizeObserver = undefined;
      if (!rootElement || typeof ResizeObserver === "undefined") return;
      rootResizeObserver = new ResizeObserver(() => {
        scheduleRecompute();
      });
      rootResizeObserver.observe(rootElement);
    });

    const unregister = editor.registerUpdateListener(() => {
      scheduleRecompute();
    });

    const unregisterSel = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        // selection change 不一定触发 updateListener，手动刷新 allRects / 菜单锚点
        recompute();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    // 点击拖拽手柄只会更新 selectedKeys，不会触发 Lexical update / SELECTION_CHANGE，
    // 若不在此主动 recompute，allRects 会滞后到「下次选区变化」才更新。
    let mountRaf = window.requestAnimationFrame(() => {
      mountRaf = 0;
      recompute();
    });

    return () => {
      if (scheduleRaf) window.cancelAnimationFrame(scheduleRaf);
      if (mountRaf) window.cancelAnimationFrame(mountRaf);
      rootResizeObserver?.disconnect();
      removeRootListener();
      unregister();
      unregisterSel();
    };
    // 勿依赖 selectedKeys：手柄点击会改 selectedKeys，导致本 effect 整段卸载重装，与菜单打开竞态。
  }, [containerRef, editor]);

  const toggleSelected = React.useCallback(
    (key: string, e: { shiftKey?: boolean; preventDefault?: () => void; stopPropagation?: () => void }) => {
      e.preventDefault?.();
      e.stopPropagation?.();
      if (!isEditable) return;
      const shift = Boolean(e.shiftKey);
      setSelectedKeys((prev) => {
        if (shift) {
          return prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key];
        }
        return [key];
      });
    },
    [isEditable],
  );

  const openMenuForKey = React.useCallback(
    (key: string) => {
      if (!isEditable) return;
      const apply = () => {
        dragMenuOpenRef.current = true;
        dragMenuKeyRef.current = key;
        const container = containerRef.current;
        if (container) {
          const pos = computeDragMenuViewportAnchorForKey(key, editor, container);
          const fallback = (() => {
            const br = container.getBoundingClientRect();
            return { top: br.top + 8, left: br.left + 8 };
          })();
          setDragMenuPos(pos ?? fallback);
        } else {
          setDragMenuPos({ top: 8, left: 8 });
        }
        setDragMenuKey(key);
        setDragMenuOpen(true);
      };
      // 晚于 blur/SELECTION_CHANGE 触发的同步 recompute，避免刚写的 ref 被「无选区」分支清掉
      queueMicrotask(apply);
    },
    [containerRef, editor, isEditable],
  );

  const onHandleClick = React.useCallback(
    (key: string, e: { shiftKey?: boolean; preventDefault?: () => void; stopPropagation?: () => void }) => {
      const shift = Boolean(e.shiftKey);
      if (!shift) {
        openMenuForKey(key);
      }
      toggleSelected(key, e);
    },
    [openMenuForKey, toggleSelected],
  );

  const onRequestCloseMenu = React.useCallback(() => {
    blockHandlePressSessionRef.current = false;
    dragMenuOpenRef.current = false;
    dragMenuKeyRef.current = null;
    setDragMenuOpen(false);
    setDragMenuKey(null);
    setDragMenuPos(null);
    setSelectedKeys([]);
  }, []);

  const beginBlockHandlePressSession = React.useCallback(() => {
    blockHandlePressSessionRef.current = true;
  }, []);

  const endBlockHandlePressSession = React.useCallback(() => {
    window.setTimeout(() => {
      blockHandlePressSessionRef.current = false;
    }, 0);
  }, []);

  React.useEffect(() => {
    if (!dragMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest('[data-floating-root="true"]')) return;
      // Portal 内点击可能落在 Positioner 包层上，未必命中 Popup 的 data-slot
      if (t.closest('[data-slot="dropdown-menu-portal"], [data-slot="dropdown-menu-positioner"]')) return;
      if (
        t.closest(
          '[data-slot="dropdown-menu"], [data-slot="dropdown-trigger"], [data-slot="dropdown-submenu-trigger"], [data-slot="dropdown-menu-content"], [data-slot="dropdown-menu-trigger"], [data-slot="dropdown-menu-sub-content"], [data-slot="dropdown-menu-sub-trigger"]',
        )
      )
        return;
      if (
        t.closest(
          '[data-slot="popover-root"], [data-slot="popover-trigger"], [data-slot="popover-dialog"], [data-slot="popover-content"], [data-slot="popover-overlay-arrow"]',
        )
      )
        return;
      if (t.closest('[data-slot="tooltip-root"], [data-slot="tooltip-trigger"], [role="tooltip"]')) return;
      if (t.closest('[data-slot="dialog-content"], [data-slot="dialog-overlay"], [data-image-crop-dialog="true"]')) return;
      if (t.closest("[data-drag-handle-menu]")) return;
      if (t.closest("[data-add-block-plus]")) return;
      onRequestCloseMenu();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [dragMenuOpen, onRequestCloseMenu]);

  return {
    activeKey,
    activeIsListItem,
    selectedKeys,
    allRects,
    dragMenuOpen,
    dragMenuPos,
    dragMenuKey,
    onHandleClick,
    onRequestCloseMenu,
    beginBlockHandlePressSession,
    endBlockHandlePressSession,
  };
}

